import { useForm } from "react-hook-form";

import {
  useErrors,
  useLoginId,
  useScreen,
} from "@auth0/auth0-acul-react/login-id";

import BeneficiaryLayout from "@/components/BeneficiaryLayout";
import Captcha from "@/components/Captcha/index";
import { ULThemeFloatingLabelField } from "@/components/form/ULThemeFloatingLabelField";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";
import ULThemeLink from "@/components/ULThemeLink";
import { useCaptcha } from "@/hooks/useCaptcha";
import { executeSafely } from "@/utils/helpers/executeSafely";
import { applyCareSuperBrand } from "@/utils/theme/careSuperBrand";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

// login-id for Beneficiary Claims ONLY.
//
// ⚠️ MEMBERS NEVER RENDER THIS. The Terraform renderer for login-id carries a
// filter matching client metadata beneficiary_app = "true"; every other client
// falls back to the STANDARD hosted login-id (the members' identifier-first
// page with member-number login and passkey autofill). That filter, not
// anything in this file, is what keeps members on the hosted screen. If it
// were ever lost, members would get this email-only form and couldn't log in
// with a member number or passkey. So verify `filters` after every apply:
//
//   node scripts/auth0-api.mjs prompts/login-id/screen/login-id/rendering
//
// The beneficiary client has only the passwordless `email` connection, so
// the identifier is always an email address and the next screen is the code.
interface LoginValues {
  username: string;
  captcha: string;
}

function BeneficiaryLoginId() {
  const loginId = useLoginId();
  const screen = useScreen();
  const { errors, hasError, dismiss } = useErrors();

  applyAuth0Theme(loginId);
  applyCareSuperBrand();
  document.title = "Log in | CareSuper Beneficiary Claims";

  const form = useForm<LoginValues>({
    defaultValues: { username: "", captcha: "" },
    reValidateMode: "onBlur",
  });
  const {
    formState: { isSubmitting },
  } = form;

  const { captchaConfig, captchaProps, captchaValue, isCaptchaSolved } =
    useCaptcha(screen.captcha || undefined, "Enter the code shown above*");
  const captchaRequired = !!screen.isCaptchaAvailable && !!captchaConfig;

  const generalErrors = errors.byType("auth0").filter((err) => !err.field);
  const usernameSDKError =
    errors.byField("username")[0]?.message ||
    errors.byField("email")[0]?.message;
  const captchaSDKError = errors.byField("captcha")[0]?.message;

  const onSubmit = async (data: LoginValues) => {
    const captcha = captchaValue ?? data.captcha;
    await executeSafely("Beneficiary login", () =>
      loginId.login({
        username: data.username.trim(),
        ...(screen.isCaptchaAvailable && captcha ? { captcha } : {}),
      })
    );
  };

  return (
    <BeneficiaryLayout
      title="Log in"
      subtitle="Enter your email address and we'll send you a code."
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {hasError && generalErrors.length > 0 && (
            <div className="space-y-3 mb-4">
              {generalErrors.map((error) => (
                <ULThemeAlert
                  key={error.id}
                  variant="destructive"
                  onDismiss={() => dismiss(error.id)}
                >
                  <ULThemeAlertTitle>{error.message}</ULThemeAlertTitle>
                </ULThemeAlert>
              ))}
            </div>
          )}

          <FormField
            control={form.control}
            name="username"
            rules={{
              required: "This field is required",
              validate: (v: string) =>
                /^\S+@\S+\.\S+$/.test(v.trim())
                  ? true
                  : "Enter a valid email address",
            }}
            render={({ field, fieldState }) => (
              <FormItem>
                <ULThemeFloatingLabelField
                  {...field}
                  label="Email address*"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  error={!!fieldState.error || !!usernameSDKError}
                />
                <ULThemeFormMessage
                  sdkError={usernameSDKError}
                  hasFormError={!!fieldState.error}
                />
              </FormItem>
            )}
          />

          {screen.isCaptchaAvailable && captchaConfig && (
            <Captcha
              control={form.control}
              name="captcha"
              captcha={captchaConfig}
              {...captchaProps}
              sdkError={captchaSDKError}
              rules={{ required: "This field is required" }}
            />
          )}

          <ULThemeButton
            type="submit"
            className="w-full"
            disabled={isSubmitting || (captchaRequired && !isCaptchaSolved)}
          >
            Continue
          </ULThemeButton>
        </form>
      </Form>

      {screen.signupLink ? (
        <div className="mt-6 text-center">
          <span className="pr-1 text-body-text text-(length:--ul-theme-font-body-text-size) font-body">
            New here?
          </span>
          <ULThemeLink href={screen.signupLink}>Start a claim</ULThemeLink>
        </div>
      ) : null}
    </BeneficiaryLayout>
  );
}

export default BeneficiaryLoginId;
